import TaskModel from '@/state/models/taskModel';
import QualificationModel from '@/state/models/qualificationModel';
import SearchCriteriaModel from '@/state/models/searchCriteriaModel';
import { restService } from '@/helpers/services/restService';
import { authHeader } from '../auth/authHeader';
import i18n from '@/i18n';
import router from '@/router';
import { toastrService } from '../notification/toastrService';
import { multiselectHelper } from '@/helpers/utils/multiselectHelper';
import Swal from 'sweetalert2';
import { dateHelper } from '@/helpers/utils/dateHelper';


const STATUS_READY_TO_ASSIGN = "READY_TO_ASSIGN";
const STATUS_PUBLISHED = "PUBLISHED";
const STATUS_REPLIED = "REPLIED";
const STATUS_ASSIGNED = "ASSIGNED";
const STATUS_COMPLETED = "COMPLETED";
const STATUS_CANCELED = "CANCELED";

const FREE_CHARACTERS_IN_SMS = process.env.VUE_APP_SMS_CHARACTER_LIMIT;

const taskUrl: string = restService.buildURL(process.env.VUE_APP_API_SUPERVISOR_TASK_URL);

const talentSearchUrl: string = restService.buildURL(process.env.VUE_APP_API_TALENT_SEARCH_URL);

const qualificationsUrl: string = restService.buildURL(process.env.VUE_APP_API_MANDATORY_QUALIFICATIONS_URL);


/**
 * Fetch talents that match the requirements for a new task
 * 
 * @param searchCriteria 
 */
async function searchForTalents(searchCriteria: SearchCriteriaModel) {
  const foundTalents: SearchCriteriaModel = await restService.post(talentSearchUrl, searchCriteria);

  return foundTalents;
}

/**
 * Fetch all open tasks
 */
async function fetchOpenTasks() {
  const url = `${taskUrl}?excludedState=COMPLETED&excludedState=CANCELED`;
  const openTasks: TaskModel[] = await restService.get(url);

  return openTasks;
}

/**
 * Fetch all completed tasks
 */
async function fetchCompletedTasks() {
  const url = `${taskUrl}?state=COMPLETED&state=CANCELED`;
  const completedTasks: TaskModel[] = await restService.get(url);

  return completedTasks;
}

/**
 * Complete given task
 *
 * @param task
 */
async function completeTask(task: TaskModel) {
  const completeTaskUrl = `${taskUrl}/${task.id}/complete`;

  const isTaskCompleted = await restService.post(completeTaskUrl);

  if (isTaskCompleted !== undefined) {
    router.push({
      name: "tasks-list",
      params: {
        toastrMessage: i18n.t("tasks.status.successfully-completed-swal-message", { taskTitle: task.title }).toString(),
        toastrType: toastrService.SUCCESS_TYPE
      }
    });
  }
}

/**
 * Cancel given task
 *
 * @param task
 */
async function cancelTask(task: TaskModel) {
  const cancelTaskUrl = `${taskUrl}/${task.id}/cancel`;

  const isTaskCanceled = await restService.post(cancelTaskUrl);

  if (isTaskCanceled !== undefined) {
    return {
        toastrMessage: `${i18n.t("tasks.status.cancel-success", { taskTitle: task.title })}`,
        toastrType: toastrService.SUCCESS_TYPE
      }
  }

  return {
      toastrMessage: `${i18n.t("tasks.status.cancel-error", { taskTitle: task.title })}`,
      toastrType: toastrService.ERROR_TYPE
    };
}

/**
 * Fetch available mandatory qualifications from API
 */
async function fetchMandatoryQualifications() {
  const qualifications: QualificationModel[] = await restService.get(qualificationsUrl);

  if (!qualifications) {
    return [];
  }

  return qualifications;
}

async function fetchTaskApplicants(taskId: string) {
  const taskApplicantsUrl = `${taskUrl}/${taskId}/applicants`;

  return await restService.get(taskApplicantsUrl);
}

async function fetchTaskDeniers(taskId: string) {
  const taskDeniersUrl = `${taskUrl}/${taskId}/deniers`;

  return await restService.get(taskDeniersUrl);
}


async function fetchTaskTalentsOrdered(taskId: string) {
  const taskDeniersUrl = `${taskUrl}/${taskId}/ordered-talents`;

  return await restService.get(taskDeniersUrl);
}

async function fetchTaskTalents(taskId: string) {
  const taskTalentsUrl = `${taskUrl}/${taskId}/talents`;

  return await restService.get(taskTalentsUrl);
}

async function fetchTaskDetails(taskId: string) {
  const taskApplicantsUrl = `${taskUrl}/${taskId}`;

  return await restService.get(taskApplicantsUrl);
}

async function assignTask(task: TaskModel, assigneeIds: string[]) {
  const url = `${taskUrl}/${task.id}/assign`;
  const errorMessage = `${i18n.t(
    "tasks.status.error-while-assigning-swal-message",
    { taskTitle: task.title }
  )}`;

  const taskIsAssigned = await restService.post(
    url,
    { applicantId: assigneeIds[0] },
    authHeader(),
    errorMessage
  );

  if (taskIsAssigned !== undefined) {
    router.push({
      name: "tasks-list",
      params: {
        toastrMessage: `${i18n.t("tasks.status.successfully-assigned-swal-message", { taskTitle: task.title })}`,
        toastrType: toastrService.SUCCESS_TYPE
      }
    });
  }
}

async function fetchTaskAssignee(taskId: string) {
  const taskAssigneeUrl: string = `${taskUrl}/${taskId}/assignee`;

  return await restService.get(taskAssigneeUrl);
}

async function checkTaskData(departmentTitle: string, talentsAmount: number, task: TaskModel) {
  return Swal.fire({
    title: i18n.t("tasks.talents-table.notify-talents-modal.title"),
    html: i18n.t(
      "tasks.talents-table.notify-talents-modal.text",
      {
        departmentTitle,
        talentsAmount,
        taskTitle: task.title,
        taskStartDate: dateHelper.toGermanDateTime(task.daterange[0]),
        taskEndDate: dateHelper.toGermanDateTime(task.daterange[1]),
        taskDescription: task.description
      }
    ),
    icon: 'warning',
    confirmButtonText: `<i class="bx bx-chat" aria-hidden="true"></i> ${i18n.t("tasks.talents-table.notify-talents-modal.confirm-button-text")}`,
    cancelButtonText: `${i18n.t("no")}`,
    showCancelButton: true,
    confirmButtonColor: '#556ee6',
    cancelButtonColor: "rgba(221, 51, 51, 0.7)",
  }).then((result) => {
    if (!result.value) {
      return false;
    }

    return true;
  });
}

/**
 * Transform the chaotic data from the form fields
 * to a nice and useable task object that can
 * be used for API calls and return it
 * 
 * @param task 
 * @param departments 
 * @param pools 
 */
function prepareTaskData(task: any, departments: any, pools: any): Object {
  return {
    "title": task.title,
    "description": task.description,
    "departmentIds": multiselectHelper.trimEverythingButIds(departments),
    "poolIds": multiselectHelper.trimEverythingButIds(pools),
    "startDate": task.daterange[0],
    "endDate": task.daterange[1],
    "mandatoryQualifications": multiselectHelper.trimEverythingButIds(task.qualifications),
  };
}

async function prepareAndPublishTask(task: any, talentIds: string[]) {
  task.talentCount = 1;
  task.talentIds = talentIds;
  delete task.departmentIds;

  const publishedTask: TaskModel = await restService.post(taskUrl, task);

  return task;
}

function getAmountOfRemainingChars(
  title: string,
  description: string,
  departmentTitle: string,
  companyTitle: string
  ) {
    let titleLength = title?.length ?? 0;
    let descriptionLength = description?.length ?? 0;
    let departmentTitleLength = departmentTitle.length ?? 0;
    let companyTitleLength = companyTitle?.length ?? 0;
  return FREE_CHARACTERS_IN_SMS - (titleLength + descriptionLength + departmentTitleLength + companyTitleLength);
}

/**
 * Return all possible status of a task as array
 * 
 * The tasks are ordered!
 */
function getAllTaskStates(): string[] {
  return [
    STATUS_PUBLISHED,
    STATUS_READY_TO_ASSIGN,
    STATUS_ASSIGNED,
    STATUS_COMPLETED,
    STATUS_CANCELED,
    // STATUS_TALENT_CANCELED,
  ];
}

/**
 * Return translation for task-status
 * @param state
 */
function getBeautifiedTaskState(state: string): string {
  return i18n.t(`tasks.${state.toLowerCase()}`).toString();
}

export const taskService = {
  searchForTalents,
  fetchOpenTasks,
  fetchCompletedTasks,
  completeTask,
  cancelTask,
  fetchMandatoryQualifications,
  fetchTaskApplicants,
  fetchTaskDeniers,
  fetchTaskTalentsOrdered,
  fetchTaskDetails,
  assignTask,
  fetchTaskTalents,
  prepareAndPublishTask,
  getAllTaskStates,
  getBeautifiedTaskState,
  fetchTaskAssignee,
  getAmountOfRemainingChars,
  checkTaskData,
}